/**
 * Parseo de un catálogo en CSV (cabecera: sku,categoria,nombre,precio,impuestos), compartido por el
 * script de importación y la app. Separador coma o punto y coma; varios impuestos en una celda van
 * con `|`. Devuelve filas válidas y errores por línea.
 */
import { normalizarSku } from './normalizar-sku';

export interface FilaCatalogo {
  sku: string;
  categoria: string;
  nombre: string;
  precio: number;
  /** Nombres de impuestos del negocio que aplican al producto. Vacío = exento. */
  impuestos: string[];
}

export interface ResultadoParseoCatalogo {
  filas: FilaCatalogo[];
  errores: { fila: number; mensaje: string }[];
}

/**
 * El separador del archivo, decidido una vez en la cabecera: el que más veces aparezca fuera de
 * comillas. Decidirlo línea a línea partiría «Copa 2 bolas, crema» en un archivo de punto y coma.
 */
function separadorDe(cabecera: string): ';' | ',' {
  let comas = 0;
  let puntoYComa = 0;
  let dentro = false;
  for (const c of cabecera) {
    if (c === '"') dentro = !dentro;
    else if (!dentro && c === ',') comas++;
    else if (!dentro && c === ';') puntoYComa++;
  }
  return puntoYComa > comas ? ';' : ',';
}

/**
 * Parte una línea respetando las comillas, como las escribe Excel: un campo entre `"` puede contener
 * el separador, y `""` dentro es una comilla literal.
 */
function partir(linea: string, sep: ';' | ','): string[] {
  const celdas: string[] = [];
  let actual = '';
  let dentro = false;
  for (let i = 0; i < linea.length; i++) {
    const c = linea[i]!;
    if (dentro) {
      if (c === '"' && linea[i + 1] === '"') {
        actual += '"';
        i++;
      } else if (c === '"') dentro = false;
      else actual += c;
    } else if (c === '"') dentro = true;
    else if (c === sep) {
      celdas.push(actual.trim());
      actual = '';
    } else actual += c;
  }
  celdas.push(actual.trim());
  return celdas;
}

/** Las líneas del archivo sin BOM y sin vacías. */
function lineasDe(texto: string): string[] {
  const sinBom = texto.charCodeAt(0) === 0xfeff ? texto.slice(1) : texto;
  return sinBom.split(/\r?\n/).filter((l) => l.trim() !== '');
}

/** Filas y columnas crudas de un CSV simple: quita el BOM de Excel y admite coma o punto y coma. */
export function parsearFilasCsv(texto: string): string[][] {
  const lineas = lineasDe(texto);
  if (lineas.length === 0) return [];
  const sep = separadorDe(lineas[0]!);
  return lineas.map((l) => partir(l, sep));
}

export function parsearCatalogoCsv(texto: string): ResultadoParseoCatalogo {
  const lineas = lineasDe(texto);
  if (lineas.length === 0) return { filas: [], errores: [{ fila: 0, mensaje: 'El archivo está vacío' }] };

  const sep = separadorDe(lineas[0]!);
  const cabecera = partir(lineas[0]!, sep).map((c) => c.toLowerCase());
  const idx = (nombre: string) => cabecera.indexOf(nombre);

  // La columna vieja llevaba un porcentaje suelto. Desde que un producto puede tener varios
  // tributos, la tarifa no identifica cuál es: se exige el nombre del impuesto del negocio.
  if (idx('impuesto_pct') >= 0) {
    return {
      filas: [],
      errores: [
        {
          fila: 1,
          mensaje:
            'La columna "impuesto_pct" ya no se usa. Reemplázala por "impuestos" con el nombre del ' +
            'impuesto tal como está en Configuración (varios separados por |). Ejemplo: IVA 19%|INC 8%',
        },
      ],
    };
  }

  for (const requerida of ['sku', 'nombre', 'precio']) {
    if (idx(requerida) < 0) {
      return { filas: [], errores: [{ fila: 1, mensaje: `Falta la columna "${requerida}". Cabecera: ${cabecera.join(', ')}` }] };
    }
  }
  const iSku = idx('sku');
  const iCat = idx('categoria');
  const iNom = idx('nombre');
  const iPre = idx('precio');
  const iImp = idx('impuestos');

  const filas: FilaCatalogo[] = [];
  const errores: { fila: number; mensaje: string }[] = [];
  lineas.slice(1).forEach((linea, n) => {
    const fila = n + 2;
    const c = partir(linea, sep);
    const sku = normalizarSku(c[iSku] ?? '');
    if (!sku || sku.length < 3) {
      errores.push({ fila, mensaje: 'SKU inválido' });
      return;
    }
    // Precio: se quitan separadores de miles (. , espacio); debe quedar solo dígitos.
    const crudoPrecio = (c[iPre] ?? '').trim();
    const soloDigitos = crudoPrecio.replace(/[.,\s]/g, '');
    if (crudoPrecio === '' || !/^\d+$/.test(soloDigitos)) {
      errores.push({ fila, mensaje: 'Precio inválido' });
      return;
    }
    const precio = Number(soloDigitos);
    filas.push({
      sku,
      categoria: (iCat >= 0 && c[iCat]) || 'General',
      nombre: (iNom >= 0 && c[iNom]) || sku,
      precio,
      impuestos:
        iImp >= 0
          ? (c[iImp] ?? '')
              .split('|')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
    });
  });
  return { filas, errores };
}
